import type { EventDispatcher, Vector2 } from 'three'
import { pointerEventToVec2 } from '@/lib/three-d-editor/utils'

export type LabelToolTypes = 'select' | 'label-creation' | 'none'

export interface Tool extends EventDispatcher {
  id: string
  onMouseMove(e: MouseEvent, mousePosition: Vector2): void
  activate(mousePosition?: Vector2): void
  deactivate(): void
  onMouseDown(e: MouseEvent): void
  onMouseDownCapture(e: MouseEvent): void
  onMouseUp(e: MouseEvent): void
  onMouseLeave(e: MouseEvent): void
  onClick(e: MouseEvent): void
}

class ToolManager {
  private _activeTool: Tool | null = null
  private _tools = new Map<string, Tool>()
  private _lastMousePosition: Vector2 | undefined

  registerTool(tool: Tool) {
    if (this._tools.has(tool.id)) {
      throw new Error(`Tool with id ${tool.id} has already been registered`)
    }
    this._tools.set(tool.id, tool)
  }

  getActiveToolId(): string | null {
    return this._activeTool?.id ?? null
  }

  setActiveTool(id: string | null) {
    if (id === null || id === 'none') {
      this._activeTool?.deactivate()
      this._activeTool = null
      return
    }
    const tool = this._tools.get(id)
    if (!tool) {
      throw new Error(`No tool with id ${id} registered. Did you forget to call registerTool()?`)
    }

    this._activeTool?.deactivate()
    this._activeTool = tool
    tool.activate(this._lastMousePosition)
  }

  attach(domElement: HTMLElement) {
    domElement.addEventListener('pointermove', this.onMouseMove, { capture: true })

    // pointerdown is used by camera-controls and can be (optionally) cancelled here
    domElement.addEventListener('pointerdown', this.onMouseDownCapture, { capture: true })
    domElement.addEventListener('pointerdown', this.onMouseDown)

    domElement.addEventListener('pointerup', this.onMouseUp, { capture: true })
    domElement.addEventListener('pointerleave', this.onMouseLeave, { capture: true })
    domElement.addEventListener('click', this.onClick, { capture: true })
  }

  private onMouseMove = (e: MouseEvent): void => {
    this._lastMousePosition = pointerEventToVec2(e)
    this._activeTool?.onMouseMove(e, this._lastMousePosition)
  }

  private onMouseDownCapture = (e: MouseEvent): void => {
    this._activeTool?.onMouseDownCapture(e)
  }

  private onMouseDown = (e: MouseEvent): void => {
    this._activeTool?.onMouseDown(e)
  }

  private onMouseUp = (e: MouseEvent): void => {
    if (!e.target || !this._activeTool) {
      return
    }
    this._activeTool?.onMouseUp(e)
  }

  private onMouseLeave = (e: MouseEvent): void => {
    this._activeTool?.onMouseLeave(e)
    this._lastMousePosition = undefined
  }

  private onClick = (e: MouseEvent) => {
    this._activeTool?.onClick(e)
  }
}

export { ToolManager }
